import { useState } from "react";
import { Heart, Leaf, Zap, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCalm } from "@/contexts/CalmModeContext";

const MONO = "'IBM Plex Mono', monospace";
const BODY = "'Albert Sans', 'Ardena', system-ui, sans-serif";
const DISPLAY = "'DM Serif Display', 'Playfair Display', Georgia, serif";

const API = process.env.REACT_APP_BACKEND_URL;

const SOCIALS = [
  { label: "X", href: "https://x.com/EmergAnimal", icon: "x" },
  { label: "Instagram", href: "https://www.instagram.com/emergencia.animal.sos/", icon: "ig" },
  { label: "Facebook", href: "https://www.facebook.com/sosemergenciaanimal", icon: "fb" },
  { label: "TikTok", href: "https://www.tiktok.com/@emergenciaanimal", icon: "tt" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/112722416", icon: "li" },
  { label: "GitHub", href: "https://github.com/emergenciaanimal", icon: "gh" },
  { label: "Telegram", href: "https://t.me/emergenciaanimal", icon: "tg" },
  { label: "WhatsApp", href: "https://whatsapp.com/channel/0029VbCWhL21t90VVBCzzp1U", icon: "wa" },
];

function SocialIcon({ type, size = 16 }) {
  const s = { width: size, height: size, fill: "currentColor" };
  const icons = {
    x: <svg viewBox="0 0 24 24" {...s}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    ig: <svg viewBox="0 0 24 24" {...s}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
    fb: <svg viewBox="0 0 24 24" {...s}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    tt: <svg viewBox="0 0 24 24" {...s}><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
    li: <svg viewBox="0 0 24 24" {...s}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    gh: <svg viewBox="0 0 24 24" {...s}><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>,
    tg: <svg viewBox="0 0 24 24" {...s}><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
    wa: <svg viewBox="0 0 24 24" {...s}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
  };
  return icons[type] || null;
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error" | "already"

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus(data.status === "already_subscribed" ? "already" : "success");
        if (data.status !== "already_subscribed") setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const msgs = {
    success: "Suscripción confirmada",
    already: "Ya estás suscrito/a",
    error: "Error, inténtalo de nuevo",
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-testid="newsletter-form">
      <div className="flex">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
          placeholder="tu@email.com"
          required
          data-testid="newsletter-email-input"
          className="flex-1 px-3 py-2.5 text-sm"
          style={{
            fontFamily: MONO,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRight: "none",
            borderRadius: "4px 0 0 4px",
            color: "#fff",
            outline: "none",
            fontSize: 13,
          }}
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="newsletter-submit-btn"
          className="px-4 py-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
          style={{
            fontFamily: MONO,
            background: "#F5A623",
            color: "#2D3561",
            border: "none",
            borderRadius: "0 4px 4px 0",
            cursor: status === "loading" ? "wait" : "pointer",
            opacity: status === "loading" ? 0.7 : 1,
          }}
        >
          {status === "loading" ? "..." : "Enviar"}
        </button>
      </div>
      {status && status !== "loading" && (
        <p className="text-[11px]" data-testid="newsletter-status"
          style={{ fontFamily: MONO, color: status === "error" ? "#f87171" : status === "already" ? "#F5A623" : "#90d890" }}>
          {msgs[status]}
        </p>
      )}
    </form>
  );
}

export default function BrutalistFooter() {
  const { isCalm, toggle } = useCalm();
  const navigate = useNavigate();

  const navy = "#2D3561";
  const lilac = "#a88efb";
  const orange = "#F5A623";

  return (
    <footer data-testid="global-footer"
      style={{ background: navy, borderTop: `1px solid ${lilac}25`, fontFamily: BODY }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14">

        {/* TOP ROW: 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">

          {/* COL 1: Brand + Teaming */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logos/logo-emergencia-animal.png" alt="EA" className="w-9 h-9 object-contain" style={{ opacity: 0.9 }} />
              <div>
                <p className="text-base font-bold" style={{ color: "#fff" }}>Emergencia Animal</p>
                <p className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: lilac }}>Nadie se queda atrás</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.5)" }}>
              Plataforma solidaria y gratuita de tecnología cívica para proteger animales en emergencias. España y Ecuador.
            </p>

            {/* Teaming CTA */}
            <a href="https://www.teaming.net/emergenciaanimal" target="_blank" rel="noopener noreferrer"
              data-testid="footer-teaming-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5"
              style={{
                fontFamily: MONO,
                background: "rgba(255,255,255,0.06)",
                color: orange,
                border: `1px solid ${orange}40`,
                borderRadius: 4,
                textDecoration: "none",
              }}>
              <Heart size={13} className="fill-current" /> Colabora con 1€/mes
              <ExternalLink size={11} style={{ opacity: 0.5 }} />
            </a>
          </div>

          {/* COL 2: Links + Social */}
          <div className="md:col-span-4">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: MONO, color: lilac }}>
              Síguenos
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  data-testid={`footer-social-${s.icon}`}
                  title={s.label}
                  className="w-9 h-9 flex items-center justify-center transition-all hover:-translate-y-0.5"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    borderRadius: 4,
                    color: "rgba(255,255,255,0.55)",
                    textDecoration: "none",
                  }}
                >
                  <SocialIcon type={s.icon} size={15} />
                </a>
              ))}
            </div>

            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: MONO, color: lilac }}>
              Enlace rápido
            </p>
            <div className="flex flex-col gap-2">
              {[
                { label: "Guías ciudadanas", to: "/recursos/ciudadania/guias", testId: "footer-guias-link" },
                { label: "Protección de datos", to: "/proteccion-datos", testId: "footer-privacy-link" },
                { label: "Ética de la IA", to: "/etica-ia", testId: "footer-ethics-link" },
                { label: "Consultoría", to: "/consultoria", testId: "footer-consultoria-link" },
              ].map((lnk) => (
                <button key={lnk.label} onClick={() => navigate(lnk.to)} data-testid={lnk.testId}
                  className="text-left text-xs transition-all hover:underline"
                  style={{ fontFamily: MONO, color: "rgba(255,255,255,0.45)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {lnk.label}
                </button>
              ))}
            </div>
          </div>

          {/* COL 3: Newsletter */}
          <div className="md:col-span-4">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ fontFamily: MONO, color: lilac }}>
              Newsletter
            </p>
            <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>
              Recibe alertas estacionales, nuevas guías y recursos para proteger a tus animales.
            </p>
            <NewsletterForm />
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="mt-10 pt-5 flex flex-col md:flex-row items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${lilac}12` }}>
          <p className="text-xs" style={{ fontFamily: MONO, color: `${lilac}66` }}>
            © 2026 Emergencia Animal · Proyecto altruista
          </p>

          <button onClick={toggle} data-testid="calm-mode-toggle"
            className="flex items-center gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider transition-all hover:-translate-y-0.5"
            style={{
              fontFamily: MONO, borderRadius: 4,
              background: isCalm ? `${lilac}20` : "rgba(255,255,255,0.06)",
              color: isCalm ? "#c8f0c8" : "#fff",
              border: `1px solid ${isCalm ? lilac + "40" : "rgba(255,255,255,0.12)"}`,
              cursor: "pointer",
            }}>
            {isCalm ? <><Leaf size={12} style={{ color: "#90d890" }} /> Modo calma activo</> :
             <><Zap size={12} style={{ color: orange }} /> Modo calma</>}
          </button>

          {isCalm && (
            <p className="text-[10px]" style={{ fontFamily: MONO, color: `${lilac}44` }}>
              Bordes suaves, colores atenuados. Pensado para neurodivergencia.
            </p>
          )}
        </div>
      </div>
    </footer>
  );
}
